import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
}

interface ChartPaletteOptions {
  violet: string[];
  blue: string[];
  green: string[];
  yellow: string[];
  red: string[];
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface TypeBackgroundCustom {
    default: string;
    paper: string;
  }
  export interface TypeButton {
    stroke: string;
    background: string;
    text: string;
  }
  export interface TypeFields {
    form: string;
    highlight: string;
  }

  export interface TypeAvatarGroup {
    background: string;
    text: string;
  }
  export interface CommonColors {
    black: string;
    white: string;
  }
  export interface TypeAlert {
    warning: string;
    error: string;
    success: string;
  }

  interface SimplePaletteColorOptions {
    lightest?: string;
    lighter: string;
    darker: string;
    darkest?: string;
  }
  interface PaletteColor {
    darkest?: string;
    lightest?: string;
    lighter: string;
    darker: string;
    light: string;
    main: string;
    dark: string;
    contrastText: string;
    header?: string
  }
  interface Palette {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
    customBackground: PaletteColor;
    black: PaletteColor;
    alert: TypeAlert;
    buttonPrimary: TypeButton;
    buttonSecondary: TypeButton;
    buttonTertiary: TypeButton;
    fields: TypeFields;
    actionBlue: TypeAction;
    actionPurple: TypeAction;
    avatarGroup: TypeAvatarGroup[];
  }
  interface PaletteOptions {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
}

declare module '@mui/material' {
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}

// SETUP COLORS

const MAIN = {
  lighter: '#D3DFF9',
  light: '#506DC0',
  main: '#223D97',
  dark: '#182E81',
  darker: '#060F48',
}

const PRIMARY = {
  lightest: '#B4BEE9',
  lighter: '#6A7DD4',
  light: '#3852C5',
  main: '#0627B7',
  dark: '#3852C5',
  darker: '#3852C5',
};

const SECONDARY = {
  lightest: '#DACBF1',
  lighter: '#B497E2',
  light: '#8F64D4',
  main: '#6A30C6',
  dark: '',
  darker: '',
};

const LIGHT_BLUE = {
  lightest: '#B3DBEF',
  lighter: '#66B8DF',
  light: '#33A0D5',
  main: '#0088CA',
  dark: '',
  darker: '',
};

const PINK = {
  lightest: '#F5EBFD',
  lighter: '#EAD8FB',
  light: '#D6B1F8',
  main: '#BA7DF4',
  dark: '',
  darker: '',
};

const BERRY = {
  lightest: '#EBC7DA',
  lighter: '#D78EB5',
  light: '#C35690',
  main: '#AF1E6B',
  dark: '',
  darker: '',
};

const GREEN = {
  lightest: '#D4EDE7',
  lighter: '#AADCCF',
  light: '#80CAB8',
  main: '#55B9A0',
  dark: '',
  darker: '',
};

const BACKGROUND = {
  main: '#EDF0F8',
  header: '#F6F7F9',
  card: '#FCFDFF'
};

const ALERT = {
  alert: '#FF9900',
  error: '#E31F30',
  success: '#66BC66',
};

const BUTTON_PRIMARY = {
  background: '#0627B7',
  text: '#FFFFFF'
}

const BUTTON_SECONDARY = {
  stroke: '#0627B7',
  background: '#EDF0F8',
  text: '#0627B7'
}

const BUTTON_TERTIARY = {
  stroke: '#6A30C6',
  background: '#6A30C6',
  text: '#FFFFFF'
}

const INFO = {
  lighter: '#D0F2FF',
  light: '#51B4FF',
  main: '#1890FF',
  dark: '#116FDB',
  darker: '#04297A',
};
const SUCCESS = {
  lighter: '#ECFBD9',
  light: '#8DD369',
  main: '#5DB73C',
  dark: '#429D2B',
  darker: '#0B570C',
};
const WARNING = {
  lighter: '#FEF8CF',
  light: '#FBD74D',
  main: '#F9C413',
  dark: '#D6A30D',
  darker: '#775103',
};
const ERROR = {
  lighter: '#FFE8D8',
  light: '#FF856D',
  main: '#FF4D3D',
  dark: '#DB2C2C',
  darker: '#7A0B26',
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

const BLUE = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#0627B7',
  600: '#04209E',
  700: '#031783',
  800: '#021169',
  900: '#010D4F',
  500_8: '#3852C5',
  500_12: alpha('#0627B7', 0.12),
  500_16: alpha('#0627B7', 0.16),
  500_24: alpha('#0627B7', 0.24),
  500_32: alpha('#0627B7', 0.32),
  500_48: alpha('#0627B7', 0.48),
  500_56: alpha('#0627B7', 0.56),
  500_80: '#B4BEE9',
};

const PURPLE = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#6A30C6',
  600: '#5F2CB2',
  700: '#53269E',
  800: '#482288',
  900: '#8F64D4',
  500_8: '#8F64D4',
  500_12: alpha('#6A30C6', 0.12),
  500_16: alpha('#6A30C6', 0.16),
  500_24: alpha('#6A30C6', 0.24),
  500_32: alpha('#6A30C6', 0.32),
  500_48: alpha('#6A30C6', 0.48),
  500_56: alpha('#6A30C6', 0.56),
  500_80: '#DACBF1',
};

const BLACK = {
  main: '#494949'
}

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const AVATAR_GROUP = [
  {
    background: '#B4BEE9',
    text: '#0627B7',
  },
  {
    background: '#DACBF1',
    text: '#6A30C6',
  },
  {
    background: '#B3DBEF',
    text: '#0088CA',
  },
  {
    background: '#F5EBFD',
    text: '#BA7DF4',
  },
  {
    background: '#EBC7DA',
    text: '#AF1E6B',
  },
  {
    background: '#D4EDE7',
    text: '#55B9A0',
  }
]

const COMMON = {
  common: { black: '#000', white: '#fff' },
  main: { ...MAIN, contrastText: '#fff' },

  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  lightBlue: { ...LIGHT_BLUE, contrastText: '#fff' },
  pink: { ...PINK, contrastText: '#fff' },
  berry: { ...BERRY, contrastText: '#fff' },
  green: { ...GREEN, contrastText: '#fff' },
  customBackground: { ...BACKGROUND, contrastText: '#fff' },
  alert: { ...ALERT, contrastText: '#fff' },
  buttonPrimary: { ...BUTTON_PRIMARY, contrastText: '#fff' },
  buttonSecondary: { ...BUTTON_SECONDARY, contrastText: '#fff' },
  buttonTertiary: { ...BUTTON_TERTIARY, contrastText: '#fff' },

  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },

  avatarGroup: AVATAR_GROUP,

  black: { ...BLACK, contrastText: '#fff' },

  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
  actionBlue: {
    hover: BLUE[500_8],
    selected: BLUE[500_16],
    disabled: BLUE[500_80],
    disabledBackground: BLUE[500_24],
    focus: BLUE[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
  actionPurple: {
    hover: PURPLE[500_8],
    selected: PURPLE[500_16],
    disabled: PURPLE[500_80],
    disabledBackground: PURPLE[500_24],
    focus: PURPLE[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  }
};


const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action },
  },
} as const;

export default palette;
